import React, { useEffect, useState, useMemo } from "react";
import { Download, Filter, BarChart3, Mail, Store, Users, Send, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, peso, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

const BRANCHES = ["", "Calamba", "Oroquieta City"];

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function AdminReports() {
  const [from, setFrom] = useState(todayISO(-30));
  const [to, setTo] = useState(todayISO());
  const [branch, setBranch] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifyList, setNotifyList] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [blasting, setBlasting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (branch) params.branch = branch;
      const { data } = await api.get("/reports/sales", { params });
      setReport(data);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifyList = async () => {
    try {
      const { data } = await api.get("/marketing/notify-list");
      setNotifyList(data.items || []);
    } catch { /* noop */ }
  };

  const loadEmailStatus = async () => {
    try {
      const { data } = await api.get("/email/status");
      setEmailStatus(data);
    } catch { setEmailStatus({ configured: false }); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter a test recipient email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/email/test", { to: testEmail });
      if (data.skipped) toast.error("Resend not configured — paste API key first");
      else if (data.ok) toast.success("Test email sent! Check inbox.");
      else toast.error(data.error || "Send failed");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const sendLowStockNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/email/low-stock-alert", {});
      if (data.skipped) toast.warning(data.reason === "no items" ? "No low-stock items right now" : "Resend not configured");
      else if (data.ok) toast.success("Low-stock alert sent to admin email!");
      else toast.error(data.error || "Send failed");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const sendBlast = async () => {
    if (!notifyList.length) { toast.error("Notify list is empty"); return; }
    if (!window.confirm(`Send the grand-opening email to ${notifyList.length} subscriber(s)?`)) return;
    setBlasting(true);
    try {
      const { data } = await api.post("/email/grand-opening-blast", {});
      toast.success(`Blast complete: ${data.sent} sent, ${data.failed} failed`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBlasting(false); }
  };

  useEffect(() => { load(); loadNotifyList(); loadEmailStatus(); /* eslint-disable-next-line */ }, []);

  const downloadCsv = () => {
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/reports/sales.csv`);
    if (from) url.searchParams.set("date_from", from);
    if (to) url.searchParams.set("date_to", to);
    if (branch) url.searchParams.set("branch", branch);
    // CSV needs auth — use fetch + blob
    const token = localStorage.getItem("arbe_token");
    fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `arbe-sales-${from}-${to}${branch ? "-" + branch : ""}.csv`;
        a.click();
      });
  };

  const downloadNotifyCsv = () => {
    const headers = ["Email", "Name", "Phone", "Source", "Signed up at"];
    const rows = notifyList.map((n) => [n.email, n.name || "", n.phone || "", n.source || "", n.created_at || ""]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `arbe-notify-list-${todayISO()}.csv`;
    a.click();
  };

  const branchTotals = useMemo(() => {
    if (!report) return [];
    const map = {};
    for (const d of report.days) {
      const key = d.branch || "—";
      map[key] = (map[key] || 0) + d.total;
    }
    return Object.entries(map).map(([branch, total]) => ({ branch, total }));
  }, [report]);

  return (
    <div className="space-y-6" data-testid="admin-reports">
      {/* Filters */}
      <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5">
        <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg mb-3">
          <Filter className="h-4 w-4" /> Sales Report
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-[#738380] font-semibold uppercase tracking-wider">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-10" data-testid="report-from" />
          </div>
          <div>
            <label className="text-xs text-[#738380] font-semibold uppercase tracking-wider">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-10" data-testid="report-to" />
          </div>
          <div>
            <label className="text-xs text-[#738380] font-semibold uppercase tracking-wider">Branch</label>
            <select value={branch} onChange={(e) => setBranch(e.target.value)} className="mt-1 h-10 w-full border border-[#E1E5DF] rounded-md px-3 text-sm" data-testid="report-branch">
              {BRANCHES.map((b) => <option key={b || "all"} value={b}>{b || "All branches"}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={load} disabled={loading} className="bg-[#2F5233] hover:bg-[#1F3D24] text-white h-10" data-testid="report-apply">
              {loading ? "Loading..." : "Apply"}
            </Button>
            <Button onClick={downloadCsv} variant="outline" className="border-[#2F5233] text-[#2F5233] hover:bg-[#2F5233] hover:text-white h-10" data-testid="report-csv">
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Totals */}
      {report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-gradient-to-br from-[#2F5233] to-[#1F3D24] text-white rounded-2xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#F3C97A]">Total Sales</div>
              <div className="font-heading font-extrabold text-2xl mt-1" data-testid="report-grand-total">{peso(report.totals.total)}</div>
              <div className="text-xs opacity-80 mt-0.5">{report.totals.count} orders</div>
            </div>
            {branchTotals.map((b) => (
              <div key={b.branch} className="bg-white border border-[#E1E5DF] rounded-2xl p-4">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#738380]">
                  <Store className="h-3 w-3" /> {b.branch}
                </div>
                <div className="font-heading font-extrabold text-2xl mt-1 text-[#1C2826]">{peso(b.total)}</div>
              </div>
            ))}
          </div>

          {/* Per-day */}
          <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5">
            <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg mb-3">
              <BarChart3 className="h-4 w-4" /> Daily Sales (by branch)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F3EE] text-left">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-[#4A5D5A] text-xs uppercase tracking-wider">Date</th>
                    <th className="px-3 py-2 font-semibold text-[#4A5D5A] text-xs uppercase tracking-wider">Branch</th>
                    <th className="px-3 py-2 font-semibold text-[#4A5D5A] text-xs uppercase tracking-wider">Type</th>
                    <th className="px-3 py-2 font-semibold text-[#4A5D5A] text-xs uppercase tracking-wider text-right">Orders</th>
                    <th className="px-3 py-2 font-semibold text-[#4A5D5A] text-xs uppercase tracking-wider text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.days.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-[#738380]">No sales in this range</td></tr>
                  ) : report.days.map((d) => (
                    <tr key={`${d.day}-${d.branch}-${d.type}`} className="border-t border-[#E1E5DF]">
                      <td className="px-3 py-2 font-mono">{d.day}</td>
                      <td className="px-3 py-2">{d.branch}</td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wider text-[#738380]">{d.type}</td>
                      <td className="px-3 py-2 text-right">{d.count}</td>
                      <td className="px-3 py-2 text-right font-semibold">{peso(d.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per cashier (POS shifts) */}
          <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5">
            <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg mb-3">
              <Users className="h-4 w-4" /> POS Sales by Cashier (shift report)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F3EE] text-left">
                  <tr>
                    <th className="px-3 py-2 text-xs uppercase tracking-wider text-[#4A5D5A]">Cashier</th>
                    <th className="px-3 py-2 text-xs uppercase tracking-wider text-[#4A5D5A]">Branch</th>
                    <th className="px-3 py-2 text-xs uppercase tracking-wider text-[#4A5D5A] text-right">Sales</th>
                    <th className="px-3 py-2 text-xs uppercase tracking-wider text-[#4A5D5A] text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.cashiers.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-[#738380]">No POS sales in this range</td></tr>
                  ) : report.cashiers.map((c, i) => (
                    <tr key={i} className="border-t border-[#E1E5DF]">
                      <td className="px-3 py-2 font-semibold">{c.cashier_name}</td>
                      <td className="px-3 py-2">{c.branch}</td>
                      <td className="px-3 py-2 text-right">{c.count}</td>
                      <td className="px-3 py-2 text-right font-semibold text-[#2F5233]">{peso(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per payment method */}
          <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5">
            <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg mb-3">
              Payment breakdown
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {report.payments.map((p) => (
                <div key={p.method} className="bg-[#F9F9F7] border border-[#E1E5DF] rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#738380] font-bold">{p.method}</div>
                  <div className="font-heading font-extrabold text-lg text-[#1C2826] mt-0.5">{peso(p.total)}</div>
                  <div className="text-[11px] text-[#738380]">{p.count} orders</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Email integration card */}
      <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5" data-testid="email-setup">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg">
            <Mail className="h-4 w-4" /> Email · Brevo
          </div>
          {emailStatus && (emailStatus.configured ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E8F5EE] text-[#2F5233] px-2.5 py-1 rounded-full" data-testid="email-status-ok">
              <CheckCircle2 className="h-3 w-3" /> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#FBE7E7] text-[#C62828] px-2.5 py-1 rounded-full" data-testid="email-status-off">
              <AlertCircle className="h-3 w-3" /> Not configured
            </span>
          ))}
        </div>

        {emailStatus && !emailStatus.configured ? (
          <div className="bg-[#F9F9F7] border border-[#E1E5DF] rounded-lg p-3 text-xs text-[#4A5D5A] leading-relaxed">
            <div className="font-bold text-[#1C2826] text-sm mb-1">How to enable email (5 min):</div>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Sign up for free at <a className="text-[#2F5233] underline" href="https://app.brevo.com/account/register" target="_blank" rel="noreferrer">brevo.com</a> (300 emails/day free, no credit card)</li>
              <li>Dashboard → profile → <strong>SMTP & API → API Keys</strong> → <strong>Generate a new API key (v3)</strong> → copy the <span className="font-mono">xkeysib-...</span> string</li>
              <li>Send the API key to your developer — it gets stored in <span className="font-mono">BREVO_API_KEY</span></li>
              <li>Verify your <span className="font-mono">arbeminimart.com</span> domain in Brevo → Senders & IP → Domains. Brevo uses <strong>TXT-only</strong> records (no MX), which works with your Wix DNS.</li>
            </ol>
          </div>
        ) : emailStatus && (
          <>
            <div className="text-xs text-[#4A5D5A] mb-3 leading-relaxed">
              Sender: <span className="font-mono text-[#1C2826]">{emailStatus.sender}</span>
              {!emailStatus.domain_verified && (
                <span className="block mt-1 text-[#C62828]">⚠ Domain not verified yet — emails may bounce. Verify <span className="font-mono">arbeminimart.com</span> in Brevo → Senders & IP → Domains (TXT records only, works with Wix DNS).</span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="border border-[#E1E5DF] rounded-xl p-3">
                <div className="text-xs font-bold text-[#1C2826] mb-1.5">Send a test email</div>
                <div className="flex gap-2">
                  <Input type="email" placeholder="you@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="h-9 text-sm" data-testid="email-test-input" />
                  <Button onClick={sendTest} disabled={sending} size="sm" className="bg-[#2F5233] hover:bg-[#1F3D24] text-white" data-testid="email-test-send">
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
              <div className="border border-[#E1E5DF] rounded-xl p-3">
                <div className="text-xs font-bold text-[#1C2826] mb-1.5">Trigger low-stock alert</div>
                <Button onClick={sendLowStockNow} disabled={sending} size="sm" variant="outline" className="border-[#C62828] text-[#C62828] hover:bg-[#C62828] hover:text-white text-xs h-9" data-testid="email-low-stock-send">
                  Send to admin now
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Notify list (Grand Opening signups) */}
      <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg">
            <Mail className="h-4 w-4" /> Grand-Opening Notify List
            <span className="text-sm font-normal text-[#738380]" data-testid="notify-list-count">({notifyList.length})</span>
          </div>
          <div className="flex items-center gap-2">
            {emailStatus?.configured && (
              <Button onClick={sendBlast} disabled={blasting || !notifyList.length} size="sm" className="bg-[#C62828] hover:bg-[#A91D1D] text-white" data-testid="notify-blast">
                {blasting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                {blasting ? "Sending..." : "Email all subscribers"}
              </Button>
            )}
            <Button onClick={downloadNotifyCsv} disabled={!notifyList.length} variant="outline" size="sm" className="border-[#2F5233] text-[#2F5233] hover:bg-[#2F5233] hover:text-white" data-testid="notify-csv">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
        {notifyList.length === 0 ? (
          <div className="py-6 text-center text-sm text-[#738380]">No signups yet. Share <span className="font-mono text-[#2F5233]">arbeminimart.com</span> to start collecting opening-day notifications.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3EE] text-left">
                <tr>
                  <th className="px-3 py-2 text-xs uppercase tracking-wider text-[#4A5D5A]">Email</th>
                  <th className="px-3 py-2 text-xs uppercase tracking-wider text-[#4A5D5A]">Phone</th>
                  <th className="px-3 py-2 text-xs uppercase tracking-wider text-[#4A5D5A]">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {notifyList.map((n) => (
                  <tr key={n.id} className="border-t border-[#E1E5DF]">
                    <td className="px-3 py-2 font-mono text-xs">{n.email}</td>
                    <td className="px-3 py-2 text-xs">{n.phone || "—"}</td>
                    <td className="px-3 py-2 text-xs text-[#738380]">{(n.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
